type License = {
  code: string;
  name: string;
  regulator: string;
  since: string;
  colors: [string, string];
  summary: string;
  nl: string;
};

/* Vlag-geïnspireerde emblemen; kleuren zijn vaste landkleuren (data), geen thema. */
const LICENSES: License[] = [
  {
    code: "CW",
    name: "Curaçao",
    regulator: "Curaçao Gaming Authority (CGA)",
    since: "Nieuwe LOK-wet sinds 24 december 2024",
    colors: ["#002b7f", "#f9e814"],
    summary:
      "Veruit de meest gebruikte licentie bij casino's zonder Cruks. Het oude systeem van masterlicenties met sublicenties is vervangen door directe vergunningen van de CGA, met strengere eisen voor KYC en anti-witwas.",
    nl: "Geen toegang tot de Nederlandse markt; geen Cruks-koppeling.",
  },
  {
    code: "KM",
    name: "Anjouan",
    regulator: "Anjouan Offshore Finance Authority",
    since: "Sterk gegroeid sinds 2023",
    colors: ["#3a75c4", "#ffc61e"],
    summary:
      "Eiland binnen de Comoren dat goedkope, snel verleende licenties uitgeeft. Veel nieuwe merken kiezen hiervoor nu Curaçao strenger is geworden. Een publiek klachtenloket of zichtbare handhaving ontbreekt grotendeels.",
    nl: "Geen toegang tot de Nederlandse markt; geen Cruks-koppeling.",
  },
  {
    code: "MT",
    name: "Malta",
    regulator: "Malta Gaming Authority (MGA)",
    since: "Actief sinds 2001",
    colors: ["#cf142b", "#ffffff"],
    summary:
      "Een serieuze Europese toezichthouder met een eigen klachtenprocedure. Grote MGA-aanbieders blokkeren Nederlandse spelers juist omdat zij hier een Ksa-vergunning nodig hebben; een Maltese licentie alleen is voor Nederland niet genoeg.",
    nl: "Alleen legaal in Nederland met daarnaast een Ksa-vergunning.",
  },
  {
    code: "KGC",
    name: "Kahnawake",
    regulator: "Kahnawake Gaming Commission",
    since: "Actief sinds 1996",
    colors: ["#7a1f2b", "#e9d8a6"],
    summary:
      "Toezichthouder van het Mohawk-territorium Kahnawake in Canada en een van de oudste online gokregulatoren. Vooral bekend bij pokerrooms en casino's die zich op Noord-Amerika richten.",
    nl: "Geen toegang tot de Nederlandse markt; geen Cruks-koppeling.",
  },
  {
    code: "CR",
    name: "Costa Rica",
    regulator: "Geen kansspeltoezichthouder",
    since: "Algemene bedrijfsregistratie",
    colors: ["#002b7f", "#ce1126"],
    summary:
      "Costa Rica kent geen specifieke online gokvergunning. Bedrijven draaien op een gewone handels- of dataverwerkingsregistratie, zonder toezicht op spellen, uitbetalingen of spelersgeld.",
    nl: "Geen kansspellicentie en geen spelersbescherming.",
  },
];

function Emblem({ code, colors }: { code: string; colors: [string, string] }) {
  const [a, b] = colors;
  return (
    <svg viewBox="0 0 64 72" className="h-14 w-12 shrink-0" role="img" aria-label={`Embleem ${code}`}>
      <path d="M32 2 60 12v24c0 17-12 28-28 34C16 64 4 53 4 36V12Z" fill={a} />
      <path d="M32 9 53 17v19c0 13-9 22-21 27-12-5-21-14-21-27V17Z" fill="none" stroke={b} strokeWidth="2.5" />
      <text x="32" y="44" textAnchor="middle" fontSize={code.length > 2 ? 15 : 19} fontWeight="700" fill={b} fontFamily="system-ui, sans-serif">
        {code}
      </text>
    </svg>
  );
}

/** Overzicht van buitenlandse licenties die casino's zonder Cruks gebruiken. */
export function OffshoreLicenses() {
  return (
    <figure id="licenties-casino-zonder-cruks" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Licenties van casino&apos;s zonder Cruks in één overzicht
      </figcaption>
      <p className="mt-1 text-xs text-muted-foreground">
        Geen van deze licenties geeft toestemming om Nederlandse spelers te bedienen; daarvoor is
        alleen een vergunning van de Kansspelautoriteit geldig.
      </p>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {LICENSES.map((l) => (
          <li key={l.name} className="flex gap-4 rounded-lg bg-secondary/60 p-4">
            <Emblem code={l.code} colors={l.colors} />
            <div className="min-w-0">
              <p className="font-semibold text-foreground">{l.name}</p>
              <p className="text-xs text-muted-foreground">
                {l.regulator} · {l.since}
              </p>
              <p className="mt-2 text-sm text-foreground">{l.summary}</p>
              <p className="mt-2 text-xs font-medium text-foreground">Voor Nederland: {l.nl}</p>
            </div>
          </li>
        ))}
        <li className="flex gap-4 rounded-lg border border-primary/40 bg-primary/5 p-4">
          <Emblem code="NL" colors={["#21468b", "#ffffff"]} />
          <div className="min-w-0">
            <p className="font-semibold text-foreground">Nederland (ter vergelijking)</p>
            <p className="text-xs text-muted-foreground">Kansspelautoriteit (Ksa) · sinds 1 oktober 2021</p>
            <p className="mt-2 text-sm text-foreground">
              De enige vergunning waarmee een casino legaal Nederlandse spelers mag bedienen, met
              verplichte Cruks-controle, limieten, klachtenregeling en gescheiden spelerstegoeden.
            </p>
            <p className="mt-2 text-xs font-medium text-foreground">Alle casino&apos;s in onze toplijst</p>
          </div>
        </li>
      </ul>
    </figure>
  );
}
